import { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { Breadcrumb } from 'ui/breadcrumb';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Checkbox, CHECKBOX_STATUS } from 'ui/checkbox';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { PAGE_HEADER_VARIANTS } from 'ui/pageHeader/constants';
import { body2, colors, fontSizes, spacing, Subtitle1 } from 'ui/theme';
import { pxToRem } from 'utils/convert';

import { BlockDetails } from './table/BlockDetails';
import { UrlField } from './UrlField';
import { usePanelContext } from '../../contexts/Panel';
import { useTranslations } from '../../hooks/useTranslations';
import { ImageAttrBatch, ImageAttrs, LinkAttrs, PanelView } from '../../constants';
import { ImageAttrState } from '../../types';
import { useMovableInkBlockContext } from '../../contexts/MovableInkBlockContext';

const CheckboxField = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${spacing.half};
`;

const DetailViewBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  row-gap: ${spacing.base};
`;

const Label = styled.label`
  ${body2};
  cursor: pointer;
  margin: 0;
`;

const DetailsDimensions = styled.div`
  align-items: flex-end;
  display: flex;
  column-gap: ${spacing.half};

  > * {
    flex: 1 1 auto;
  }

  input {
    width: 100%;
  }
`;

const DetailsProportions = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing.plusHalf};
  margin-top: ${spacing.eighth};
`;

const AssetName = styled(Subtitle1)`
  margin-bottom: 0;
  margin-top: ${spacing.half};
`;

const FileDetails = styled.div`
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s12};
`;

const FileArea = css`
  flex: 0 0 auto;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

const FilePreview = styled.div<{ $imgSrc: string }>`
  background-color: ${colors.backgroundTertiary};
  background-image: url(${({ $imgSrc }) => $imgSrc});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  border: ${spacing.single} solid ${colors.grey15};
  border-radius: ${spacing.threeEights};
  height: ${pxToRem(249)};
  ${FileArea}
`;

export const testIds = {
  breadcrumb: 'mi-panel-detail-breadcrumb',
  btnResetDimensions: 'mi-panel-detail-btn-reset',
  checkboxAR: 'mi-panel-detail-checkbox-ar',
  detailsNode: 'mi-panel-detail-file-details-node',
  detailsSelected: 'mi-panel-detail-file-details-selected',
  htmlAttrs: 'mi-panel-detail-html-attrs',
  inputAltText: 'mi-panel-detail-input-alt-text',
  inputHeight: 'mi-panel-detail-input-height',
  inputStyles: 'mi-panel-detail-input-styles',
  inputWidth: 'mi-panel-detail-input-width',
  preview: 'mi-panel-detail-preview',
  previewReplace: 'mi-panel-detail-preview-replace',
};

export const PanelViewDetail = () => {
  const { t } = useTranslations();
  const {
    canStyle,
    imageAttrState,
    imgMetadata,
    linkAttrState,
    onInsert,
    selectedEditorImageNode,
    setImageAttrState,
    setNonCustomizableAttrs,
    setPanelView,
  } = usePanelContext();

  const { selectedBlock } = useMovableInkBlockContext();
  const [lockAspectRatio, setLockAspectRatio] = useState<boolean>(true);

  const handleNonCustomizableAttrs = (image: HTMLImageElement) => {
    const customizableAttrs = [
      ImageAttrs.ALT_DESC,
      ImageAttrs.HEIGHT,
      ImageAttrs.SOURCE,
      ImageAttrs.STYLES,
      ImageAttrs.WIDTH,
    ] as string[];
    const otherAttrs: Record<string, string> = {};

    // the data-mce attributes are not applicable to the DOMUtils used to set the new attributes
    image.getAttributeNames().forEach((attrName) => {
      if (customizableAttrs.indexOf(attrName) === -1 && !attrName.startsWith('data-mce')) {
        otherAttrs[attrName] = image.getAttribute(attrName) || '';
      }
    });
    setNonCustomizableAttrs(otherAttrs);
  };

  const handleResponsiveSizing = (value: string, dimension: ImageAttrs) => {
    const altDim = dimension === ImageAttrs.HEIGHT ? ImageAttrs.WIDTH : ImageAttrs.HEIGHT;
    const size = value.replace(/[^\d]+/g, '');
    const sizeInt = parseInt(size, 10);
    setImageAttrState({ type: dimension, value: size });
    // only update the alternate dimension if it was previously defined
    if (lockAspectRatio && sizeInt && imageAttrState[altDim]) {
      setImageAttrState({
        type: altDim,
        value: Math.round(
          altDim === ImageAttrs.HEIGHT
            ? sizeInt / imgMetadata.naturalAspectRatio
            : sizeInt * imgMetadata.naturalAspectRatio,
        ).toString(),
      });
    }
  };

  useEffect(() => {
    if (selectedEditorImageNode) {
      handleNonCustomizableAttrs(selectedEditorImageNode);
    }
  }, []);

  const handleResetDimensions = () => {
    const attrValues = {} as ImageAttrState;

    if (selectedBlock) {
      attrValues[ImageAttrs.HEIGHT] = imgMetadata.height.toString();
      attrValues[ImageAttrs.WIDTH] = imgMetadata.width.toString();
    } else if (selectedEditorImageNode) {
      attrValues[ImageAttrs.HEIGHT] = selectedEditorImageNode?.naturalHeight.toString();
      attrValues[ImageAttrs.WIDTH] = selectedEditorImageNode?.naturalWidth.toString();
    }
    setImageAttrState({ type: ImageAttrBatch.UPDATE_BATCH, value: attrValues });
  };

  return (
    <DetailViewBody>
      <section data-test={testIds.breadcrumb}>
        <Breadcrumb
          isButton
          breadcrumb={{
            content: t('link.contentList'),
            onClick: () => setPanelView(PanelView.List),
          }}
          variant={PAGE_HEADER_VARIANTS.SECONDARY}
        />
      </section>
      <section data-test={testIds.preview}>
        <FilePreview $imgSrc={imageAttrState[ImageAttrs.SOURCE]} />
        <AssetName>{imgMetadata.name}</AssetName>
        <FileDetails data-test={selectedBlock ? testIds.detailsSelected : testIds.detailsNode}>
          <BlockDetails metadata={imgMetadata} />
        </FileDetails>
      </section>
      <UrlField
        url={imageAttrState[ImageAttrs.SOURCE]}
        label={t('input.url.label.image')}
        canCopy
      />
      {linkAttrState[LinkAttrs.HREF] && (
        <UrlField url={linkAttrState[LinkAttrs.HREF]} label={t('input.url.label.link')} canCopy />
      )}
      {!!onInsert && (
        <section data-test={testIds.htmlAttrs}>
          <FormField inputId="asset-form-alt-text" labelText={t('input.altText.label')}>
            <InputText
              data-test={testIds.inputAltText}
              id="asset-form-alt-text"
              onChange={(value: string) => setImageAttrState({ type: ImageAttrs.ALT_DESC, value })}
              type="text"
              value={imageAttrState[ImageAttrs.ALT_DESC]}
            />
          </FormField>
          {canStyle && (
            <>
              <DetailsDimensions>
                <FormField inputId="asset-form-width" labelText={t('input.width.label')}>
                  <InputText
                    data-test={testIds.inputWidth}
                    id="asset-form-width"
                    onChange={(value: string) => handleResponsiveSizing(value, ImageAttrs.WIDTH)}
                    Suffix={<span>{t('abbreviation.pixel')}</span>}
                    type="text"
                    value={imageAttrState[ImageAttrs.WIDTH]}
                  />
                </FormField>
                <FormField inputId="asset-form-height" labelText={t('input.height.label')}>
                  <InputText
                    data-test={testIds.inputHeight}
                    id="asset-form-height"
                    onChange={(value: string) => handleResponsiveSizing(value, ImageAttrs.HEIGHT)}
                    Suffix={<span>{t('abbreviation.pixel')}</span>}
                    type="text"
                    value={imageAttrState[ImageAttrs.HEIGHT]}
                  />
                </FormField>
              </DetailsDimensions>
              <DetailsProportions>
                <CheckboxField>
                  <Checkbox
                    data-test={testIds.checkboxAR}
                    id="lockAspectRatio"
                    name="lockAspectRatio"
                    onClick={() => setLockAspectRatio((ar) => !ar)}
                    status={lockAspectRatio ? CHECKBOX_STATUS.CHECKED : CHECKBOX_STATUS.UNCHECKED}
                  />
                  <Label htmlFor="lockAspectRatio" onClick={() => setLockAspectRatio((ar) => !ar)}>
                    {t('label.keepProportions')}
                  </Label>
                </CheckboxField>
                <Button
                  data-test={testIds.btnResetDimensions}
                  onClick={handleResetDimensions}
                  size={BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.GHOST}
                >
                  <span>{t('button.reset')}</span>
                </Button>
              </DetailsProportions>
              <FormField
                helpText={t('input.customStyles.helpText')}
                inputId="asset-form-custom-styles"
                labelText={t('input.customStyles.label')}
              >
                <InputText
                  data-test={testIds.inputStyles}
                  id="asset-form-custom-styles"
                  onChange={(value: string) =>
                    setImageAttrState({ type: ImageAttrs.STYLES, value })
                  }
                  type="text"
                  value={imageAttrState[ImageAttrs.STYLES]}
                />
              </FormField>
            </>
          )}
        </section>
      )}
    </DetailViewBody>
  );
};
